/**
 * Step 4: Review - Edit vendor questions before RFP
 */
import { useWizard } from './WizardContext';
import GenerationLock from './GenerationLock';
import { ReadOnlyStepBanner } from './WizardBanners';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, Table, Loader2, MinusCircle } from 'lucide-react';
import { removalReason } from '@/lib/vendorQuestionRemoval';

/**
 * REMOVED VENDOR QUESTIONS (F5, 2026-08-29 - Ashok ruling 3, 2026-08-26).
 *
 * A question the applicability verdict marked not-applicable does not reach
 * the vendor. It is shown HERE instead of vanishing: "a question vanishing
 * between generation and review with no trace gets rediscovered as a defect".
 *
 * DELIBERATELY INERT. No Textarea, no delete button, no restore control (V1
 * ruling 4) - a buyer who wants the question back uses Add Question, and what
 * they add is honestly buyer-authored, which is a cleaner record than
 * un-removing a system decision. Rendered in BOTH the editable and read-only
 * paths, because Review is read-only on every revisit and the trace must
 * survive one.
 *
 * The question shown is the ORIGINAL - that is the one the buyer needs to
 * recognise. `revised` is omitted on a removal by contract.
 */
const RemovedQuestions = ({ removed }) => {
  if (!removed || removed.length === 0) return null;

  return (
    <div className="space-y-3 mb-8" data-testid="removed-questions-section">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
        Removed — Not Included in the RFP ({removed.length})
      </div>
      {removed.map((entry, i) => (
        <div
          key={i}
          className="bg-zinc-50 border border-zinc-200 border-dashed p-4 rounded-sm"
          data-testid={`removed-question-${i}`}
        >
          <div className="flex items-start gap-3">
            <MinusCircle className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="mb-2">
                <span className="bg-zinc-200 text-zinc-600 text-xs px-1.5 py-0.5 rounded">
                  Removed
                </span>
              </div>
              <p className="text-sm text-zinc-500 line-through mb-2">
                {(entry && entry.original) || ''}
              </p>
              <p className="text-sm text-zinc-600" data-testid={`removed-question-reason-${i}`}>
                {removalReason(entry)}
              </p>
            </div>
          </div>
        </div>
      ))}
      <p className="text-xs text-zinc-500">
        These questions will not appear in the RFP and will not be sent to vendors.
        To ask something similar, use Add Question above.
      </p>
    </div>
  );
};

const StepReview = () => {
  const {
    loading,
    revisedVendorQuestions,
    removedVendorQuestions,
    revisionSummary,
    editableQuestions,
    setCurrentStep,
    handleFinalizeQuestions,
    handleQuestionEdit,
    handleAddQuestion,
    handleRemoveQuestion,
    isStepPreviouslyCompleted,
    isStepUnlocked,
    unlockStepForEdit
  } = useWizard();

  const isReadOnly = isStepPreviouslyCompleted('review') && !isStepUnlocked('review');

  if (isReadOnly) {
    return (
      <div className="relative">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">
            Review Vendor Questions
          </h2>
          <p className="text-zinc-600">
            Edit the vendor questions before they go into the RFP.
          </p>
        </div>

        <ReadOnlyStepBanner stepId="review" onEdit={() => unlockStepForEdit('review', editableQuestions)} />

        <div className="space-y-4 mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Vendor Questions ({editableQuestions.length})
          </div>
          {editableQuestions.map((question, i) => (
            <div key={i} className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm flex gap-3">
              <span className="text-[#0055FF] font-bold text-sm">{i + 1}.</span>
              <p className="text-sm text-zinc-700">{question}</p>
            </div>
          ))}
        </div>

        <RemovedQuestions removed={removedVendorQuestions} />

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(2)} className="border-zinc-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={() => setCurrentStep(4)}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="continue-readonly-review-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" /> Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <GenerationLock active={loading} />
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-zinc-900 mb-2">
          Review Vendor Questions
        </h2>
        <p className="text-zinc-600">
          Edit the vendor questions before they go into the RFP.
        </p>
      </div>

      {revisionSummary && (
        <div className="bg-blue-50 border border-blue-200 p-4 mb-6 rounded-sm">
          <p className="text-sm text-blue-700">{revisionSummary}</p>
        </div>
      )}
      
      <div className="space-y-4 mb-8">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Vendor Questions ({editableQuestions.length})
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleAddQuestion} 
            className="border-zinc-200" 
            data-testid="add-question-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Question
          </Button>
        </div>
        
        {editableQuestions.map((question, i) => {
          const revision = revisedVendorQuestions[i];
          const wasRevised = revision && revision.original !== revision.revised;
          
          return (
            // Keyed by stable index, never by the editable question text
            // itself - a content-derived key changes on every keystroke,
            // which forces React to unmount/remount this whole subtree
            // (including the Textarea below) and drops focus mid-word.
            <div key={i} className="bg-white border border-zinc-200 p-4 rounded-sm">
              <div className="flex items-start gap-3">
                <span className="text-[#0055FF] font-bold text-sm mt-2">{i + 1}.</span>
                <div className="flex-1">
                  {wasRevised && (
                    <div className="mb-2 text-xs text-zinc-500">
                      <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mr-2">Revised</span>
                    </div>
                  )}
                  <Textarea
                    value={question}
                    onChange={(e) => handleQuestionEdit(i, e.target.value)}
                    placeholder="Enter vendor question..."
                    className="min-h-[70px] bg-zinc-50 border-zinc-200"
                    data-testid={`edit-question-${i}`}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveQuestion(i)}
                  // Discoverability fix (2026-07-27): same watermark
                  // treatment as StepQuestions' business questions - faded
                  // at rest, full opacity on hover - for visual consistency
                  // across every deletable question list. Vendor questions
                  // have no mandatory concept (handleRemoveQuestion has no
                  // guard), so there's no lock-icon case here.
                  className="text-zinc-400 opacity-40 hover:opacity-100 hover:text-red-500 transition-opacity"
                  data-testid={`remove-question-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <RemovedQuestions removed={removedVendorQuestions} />

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(2)} disabled={loading} className="border-zinc-200">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={() => handleFinalizeQuestions()}
          disabled={loading || editableQuestions.filter(q => q.trim()).length === 0}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6" 
          data-testid="finalize-questions-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Table className="w-4 h-4 mr-2" />}
          {loading ? 'Processing...' : 'Generate Line Items'}
        </Button>
      </div>
    </div>
  );
};

export default StepReview;
